<script setup>
import TransitionFadeIn from '@/components/shared/transitions/TransitionFadeIn.vue'
import AppLoader from '@/components/app/AppLoader.vue'
import AppError from '@/components/app/AppError.vue'
import FlashMessageContainer from '@/components/shared/core/FlashMessageContainer.vue'
// import NewVersionModal from '@/components/app/NewVersionModal.vue'
import {openUrl} from '@/helpers/url.js'
import {useEnv} from '@/composables/use-env.js'
import {useTeams} from '@/composables/use-teams.js'
import {useLoading} from '@/composables/use-loading.js'
import {useRouting} from '@/composables/use-routing.js'
import {useDocument} from '@/composables/use-document.js'
import {useAppTheme} from '@/composables/use-app-theme.js'
import {useNavigation} from '@/composables/use-navigation.js'
import {useCurrentUser} from '@/composables/use-current-user.js'
import {useVersionCheck} from '@/composables/use-version-check.js'
import {useErrorHandling} from '@/composables/use-error-handling.js'
import {useAuthentication} from '@/composables/use-authentication.js'
import {useCurrentOrganization} from '@/composables/use-current-organization.js'
import {useTimezoneDetection} from '@/composables/use-timezone-detection.js'
import {useSegment} from '@/composables/use-segment.js'
import {useEventListener} from '@vueuse/core'
import {ref, watchEffect, onMounted} from 'vue'

//Refs
const hasError = ref(false)
const notAuthenticated = ref(false)

//Composables
const {theme} = useAppTheme()
const {identify} = useSegment()
const {pushRoute, router} = useRouting()
const {loadUser, user, updateUser} = useCurrentUser()
const {setDocumentTheme} = useDocument()
const {isLoading, setLoading} = useLoading(true)
const {loadOrganization} = useCurrentOrganization()
const {browserTimezone} = useTimezoneDetection()
const {followTeamsDeepLink} = useNavigation()
const {env, origin, gitHash, apiBaseUrl, isProduction} = useEnv()
const {startVersionCheck/*, hasNewVersion*/} = useVersionCheck()
const {initializeTeams, isInTeams, deepLink} = useTeams()
const {processError, registerGlobalErrorListeners} = useErrorHandling()
const {authenticateWithTeams, setAuthenticated, obtainCsrfToken} = useAuthentication()

//Start version check
startVersionCheck()

//Register global error listeners
registerGlobalErrorListeners()

//Global click handler to handle both internal and external links (present in markdown)
//This intercepts magic links to the main app (like https://arist.app/e/84fdabd3-7e3f-4f71-a17d-ff25d8d84a0d)
//and re-routes those to the enrollment details page in the messenger app itself.
//For all other external links, it force opens them in a new window to bypass
//any security restrictions on opening those in the iframe of the Teams tab app.
//Teams app deep links are left alone to let Teams handle them appropriately.
const enrollmentRegex = new RegExp(
  `${apiBaseUrl.value}/e/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})`
)
useEventListener(document, 'click', e => {
  if (e.target.tagName.toLowerCase() === 'a') {
    const match = e.target.href.match(enrollmentRegex)
    if (match) {
      e.preventDefault()
      const enrollmentId = match[1]
      pushRoute({name: 'manage-enrollment', params: {enrollmentId}})
    }
    else if (isInTeams.value && !e.target.href.startsWith('https://teams.microsoft.com/l/entity')) {
      e.preventDefault()
      openUrl(e.target.href, null, true)
    }
  }
})

//Watch for theme changes
watchEffect(() => setDocumentTheme(theme.value))

//Reload page after 30 seconds if error encountered
watchEffect(() => {
  if (hasError.value && isProduction.value) {
    setTimeout(() => window.location.reload(), 30000)
  }
})

//Mount logic
onMounted(async () => {

  //Log
  if (typeof process === 'undefined' || !process.env.VITEST) {
    console.log(`---------------------------------------------------------------`)
    console.log(`Running in ${env.value} environment on ${origin.value} 🚀`)
    console.log(`Version: ${gitHash.value}`)
    console.log(`---------------------------------------------------------------`)
  }

  //Wait for router to be ready (so that query params are available)
  await router.isReady()

  //Wrap in error handler
  try {

    //This has to be done before authentication so that we can establish whether
    //we're running in Teams or not, which has an impact on the authentication flow
    await initializeTeams()

    //Actively authenticate if within teams. If not in teams, we'll be
    //authenticated automatically via the 401 handler in the base API.
    if (isInTeams.value) {

      //Wrapping in a separate try block here, because we want any error that the
      //API responds with to be treated as an authentication error, rather than a
      //generic server error. The backend will in some cases, especially with bad
      //data, respond with a 500 instead, which results to the user seeing the
      //generic error page, rather than the auth specific one.
      try {
        await authenticateWithTeams()
      }

      //Force status to 401 and rethrow, to handle it properly in the parent catch block
      catch (error) {
        error.status = 401
        throw error
      }
    }

    //Load all the things
    await Promise.all([
      loadUser(),
      loadOrganization(),
    ])

    //Identify for analytics now
    identify()

    //If we succeeded to load the user, we're already authenticated via cookies
    //Set as authenticated manually, and obtain a CSRF token for non-GET requests
    if (!isInTeams.value && user.value) {
      setAuthenticated(true)
      await obtainCsrfToken()
    }

    //Navigate to course page if deep link from Teams detected
    if (isInTeams.value && deepLink.value) {
      followTeamsDeepLink(deepLink.value)
    }

    //Clear loading state
    setLoading(false)
  }
  catch (error) {
    processError(error)
    if (error.isUnauthenticatedError) {
      notAuthenticated.value = true
    }
    else {
      hasError.value = true
    }
  }
})

//Update user timezone
watchEffect(() => {
  if (user.value && !user.value.timezone && browserTimezone.value) {
    updateUser({timezone: browserTimezone.value})
  }
})
</script>

<template>
  <TransitionFadeIn>
    <AppLoader v-if="isLoading || hasError || notAuthenticated">
      <AppError
        v-if="hasError"
        error-title="Sorry, there was an error loading the app"
        error-message="Please try reloading the page or try again later."
      />
      <AppError
        v-else-if="notAuthenticated && isInTeams"
        error-title="We couldn’t authenticate your Teams account yet"
        error-message="To get started, please make sure your administrator has provided you with the necessary permissions."
      />
      <AppError
        v-else-if="notAuthenticated"
        error-title="Not authenticated"
        error-message="Redirecting you to the login page..."
        :show-support-link="false"
      />
    </AppLoader>
  </TransitionFadeIn>
  <RouterView
    v-if="!isLoading && !hasError"
    :key="String($route.path)"
  />
  <!-- <NewVersionModal :is-open="hasNewVersion" /> -->
  <FlashMessageContainer />
</template>
